/**
 * Umami analytics loader — RGPD-compliant.
 *
 * Script is injected ONLY when the user explicitly enabled the
 * "mesure d'audience" toggle in the cookie banner. If they later
 * revoke consent, the script tag is removed.
 *
 * Env vars (set in /app/frontend/.env once your Umami instance is live):
 *   REACT_APP_UMAMI_SCRIPT_URL  e.g. https://cloud.umami.is/script.js
 *   REACT_APP_UMAMI_WEBSITE_ID  e.g. 1a2b3c4d-...
 *
 * If either env var is missing we silently no-op — safe to ship.
 */

const STORAGE_KEY = "em-cookie-consent-v1";
const SCRIPT_ID = "em-umami-script";

function hasConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    return JSON.parse(raw)?.analytics === true;
  } catch {
    return false;
  }
}

function loadUmami() {
  if (document.getElementById(SCRIPT_ID)) return;
  const url = process.env.REACT_APP_UMAMI_SCRIPT_URL;
  const websiteId = process.env.REACT_APP_UMAMI_WEBSITE_ID;
  if (!url || !websiteId) {
    // Configured later — keep silent in console
    return;
  }
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.async = true;
  s.defer = true;
  s.src = url;
  s.setAttribute("data-website-id", websiteId);
  // Honour DNT — defence in depth
  s.setAttribute("data-do-not-track", "true");
  // No cookies, no PII, no auto-events flagged
  document.head.appendChild(s);
}

function unloadUmami() {
  const tag = document.getElementById(SCRIPT_ID);
  if (tag) tag.remove();
  // Best-effort: clear umami global if exposed
  if (window.umami) {
    try { delete window.umami; } catch { /* noop */ }
  }
}

/**
 * Call once on app boot. Wires up consent listener.
 */
export function initAnalytics() {
  if (hasConsent()) loadUmami();

  window.addEventListener("em-cookie-consent-changed", (e) => {
    const ok = !!e?.detail?.analytics;
    if (ok) loadUmami();
    else unloadUmami();
  });
}

/**
 * Track a custom event (page conversions, CTAs).
 * Safe to call even when analytics is disabled — no-op fallback.
 */
export function trackEvent(name, data = {}) {
  if (typeof window === "undefined") return;
  if (typeof window.umami?.track === "function") {
    try { window.umami.track(name, data); } catch { /* noop */ }
  }
}
